/**
 * Temporary screen-scoped design tokens for **Contacts** (`/contacts`).
 *
 * Mirrors `Contacts.dc.html` 1:1 — every colour, size, radius, spacing and type
 * value the screen needs lives here so the components never inline a literal.
 * Once every screen is re-skinned these get promoted into `src/theme` and this
 * file is deleted (see UI_IMPLEMENTATION_RULES.md § Temporary Screen Tokens).
 *
 * Field colours already made that move: they live in `theme.palette.formField`
 * (see `theme/colors.ts`) and are re-exported through `field` below so the
 * screen and the shared inputs never drift apart. The *geometry* stays local —
 * this screen's fields are 44px/10px radius against My Profile's 40px/9px, and
 * the rules say not to force one onto the other.
 */

import {
  button,
  contentCard,
  formField,
  inkfree,
  notification,
  signer,
  white,
} from "theme/colors";

export const contactsTokens = {
  /**
   * Inner gutters of the content column. The column itself is centred by
   * `CenteredAuthenticatedLayout`, the same wrapper Templates and Sign Requests
   * use, so the three pages sit on one measure.
   */
  page: {
    paddingTop: "32px",
    paddingX: "40px",
    paddingBottom: "40px",
    /**
     * Everything above and below the column, subtracted from the viewport so
     * the master-detail card can fill exactly what is left. Sourced from the
     * sticky `TopNav` and from `CenteredAuthenticatedLayout`'s own `mt`/`pb`;
     * keep in step if either changes.
     */
    topNavHeight: 64,
    /**
     * The bar's hairline sits outside that 64px min-height. The desktop column
     * absorbs it in its bottom padding, but the compact columns lock to the
     * viewport exactly, so they have to subtract it as well.
     */
    topNavBorder: 1,
    layoutMarginTop: { md: 40, lg: 48 },
    layoutPaddingBottom: 48,
  },

  /** "Contacts" title block + the two header actions. */
  header: {
    gap: "12px",
    marginBottom: "22px",
    titleColor: signer.ink,
    titleFontSize: "26px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.02em",
    subtitleColor: signer.text.subtitle,
    subtitleFontSize: "13.5px",
    subtitleMarginTop: "6px",
    /** The bolded contact count inside the subtitle. */
    countColor: signer.text.meta,
    countFontWeight: 700,
  },

  /** Outlined "Add Contact" button. */
  secondaryButton: {
    height: 44,
    paddingX: "18px",
    bg: white,
    color: signer.orange.main,
    border: signer.orange.main,
    hoverBg: signer.warn.tint,
    radius: "10px",
    fontSize: "13.5px",
    fontWeight: 700,
    gap: "9px",
    iconSize: 16,
  },

  /** Filled "Bulk Upload" button. */
  primaryButton: {
    height: 44,
    paddingX: "20px",
    bg: inkfree.main,
    hoverBg: inkfree.dark,
    color: white,
    radius: "10px",
    fontSize: "13.5px",
    fontWeight: 700,
    gap: "9px",
    iconSize: 16,
    /** Padlock shown when CSV import is not in the plan. */
    lockIconSize: 16,
  },

  /** White master-detail card holding the list and the form. */
  card: {
    bg: contentCard.bg,
    border: contentCard.border,
    radius: "16px",
    shadow: contentCard.shadow,
  },

  /** LEFT pane — search, result meta and the contact rows. */
  list: {
    width: 388,
    divider: signer.border.divider,
    /** Wrapper around the search field. */
    searchPadding: "18px 18px 14px",
    searchBg: formField.bg,
    searchBorder: formField.borderWarm,
    searchFocusBorder: signer.orange.main,
    searchRadius: "11px",
    searchFieldPadding: "11px 14px",
    searchGap: "11px",
    searchIconSize: 17,
    searchIconColor: signer.text.faint,
    searchFontSize: "14px",
    searchColor: signer.ink,
    searchPlaceholder: formField.placeholder,
    /**
     * The design's search box is a bare `<input>` with the browser's default
     * inset left in place, which is what makes the field 44px tall and sets the
     * text 2px in from the icon gap. MUI zeroes that inset, so restate it.
     */
    searchInputPadding: "1px 2px",

    /** "14 SHOWN" / "Name ⇅" strip under the search field. */
    metaPadding: "0 18px 6px",
    metaFontSize: "11px",
    metaFontWeight: 700,
    metaLetterSpacing: ".08em",
    metaColor: signer.text.faint,
    sortFontSize: "11.5px",
    sortFontWeight: 600,
    sortColor: button.disabledColor,
    sortGap: "5px",
    sortIconSize: 12,

    /** Scrollable row container. */
    scrollPadding: "4px 10px 12px",
    scrollbarWidth: "8px",
    scrollbarThumb: button.secondaryBorder,
    scrollbarRadius: "8px",

    /** One contact row. */
    rowGap: "13px",
    rowPadding: "11px 12px",
    rowRadius: "12px",
    rowMarginBottom: "2px",
    rowSelectedBg: signer.orange.tint,
    rowSelectedBorder: "#F7D9C7",
    rowHoverBg: formField.bg,
    avatarSize: 40,
    avatarFontSize: "13px",
    avatarFontWeight: 700,
    nameFontSize: "13.5px",
    nameFontWeight: 700,
    nameColor: signer.ink,
    emailFontSize: "12px",
    emailColor: signer.text.faint,
    emailMarginTop: "2px",
    dotSize: 6,
    dotColor: signer.orange.main,

    /** "No contacts match …" block inside the list. */
    emptyPadding: "48px 20px",
    emptyTitleFontSize: "13px",
    emptyTitleFontWeight: 600,
    emptyTitleColor: signer.text.meta,
    emptySubFontSize: "12px",
    emptySubMarginTop: "4px",
    emptySubColor: signer.text.faint,

    /** Inline spinner shown while the next page loads. */
    loaderPadding: "14px 0",
    loaderSize: 20,
  },

  /** RIGHT pane — the contact form. */
  detail: {
    padding: "28px 34px 40px",
    maxWidth: 820,
    /** Identity strip above the fields. */
    headGap: "14px",
    headMarginBottom: "24px",
    avatarSize: 52,
    avatarFontSize: "17px",
    avatarFontWeight: 800,
    /** Fallback tint for the "New Contact" placeholder avatar. */
    newAvatarBg: signer.surface.muted,
    newAvatarColor: signer.text.faint,
    titleFontSize: "19px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.01em",
    titleColor: signer.ink,
    subFontSize: "12.5px",
    subColor: signer.text.subtitle,
    subMarginTop: "3px",
    /** "Address" rule that splits the form. */
    sectionMarginTop: "26px",
    sectionMarginBottom: "16px",
    sectionGap: "12px",
    sectionFontSize: "13px",
    sectionFontWeight: 800,
    sectionLetterSpacing: ".02em",
    sectionColor: signer.ink,
    sectionRule: signer.border.divider,
    /** Footer action bar. */
    footerMarginTop: "32px",
    footerPaddingTop: "22px",
    footerBorder: signer.border.divider,
    footerGap: "12px",
    /** Mobile-only "back to list" affordance. */
    backButtonSize: 34,
    backIconSize: 18,
    backColor: signer.text.meta,
  },

  /** Every text input, dial-code control and select on the form. */
  field: {
    rowGap: "16px",
    columnGap: "20px",
    labelGap: "6px",
    labelFontSize: "11.5px",
    labelFontWeight: 600,
    labelColor: formField.label,
    requiredColor: formField.required,
    /** Tops up MUI's thin space before the asterisk to the design's full one. */
    asteriskGap: "2px",
    height: 44,
    radius: "10px",
    borderWidth: "1px",
    border: formField.border,
    bg: formField.bg,
    color: formField.text,
    fontSize: "14px",
    fontWeight: 500,
    paddingX: "14px",
    focusBorder: formField.focusBorder,
    hoverBorder: signer.ink,
    placeholder: formField.placeholder,
    disabledBg: formField.disabledBg,
    disabledBorder: formField.disabledBorder,
    disabledColor: formField.disabledText,
    errorFontSize: "10.5px",
    /** "4/100" counter pinned inside the right edge of a field. */
    counterFontSize: "11px",
    counterFontWeight: 600,
    counterColor: button.disabledColor,
    counterInset: "12px",
    chevronSize: 10,
    chevronColor: formField.adornment,
    /** Country select — same box, slightly larger chevron. */
    selectChevronSize: 11,
    selectFontWeight: 600,
  },

  /** "Save Changes" / "Cancel" / "Delete Contact" row. */
  formActions: {
    saveHeight: 44,
    savePaddingX: "24px",
    saveBg: inkfree.main,
    saveHoverBg: inkfree.dark,
    saveColor: white,
    saveFontSize: "14px",
    cancelPaddingX: "20px",
    cancelBg: white,
    cancelBorder: formField.border,
    cancelColor: signer.text.meta,
    cancelHoverColor: signer.ink,
    cancelHoverBorder: signer.ink,
    cancelFontSize: "13.5px",
    deletePaddingX: "18px",
    deleteBg: white,
    deleteBorder: "#E8C9C9",
    deleteColor: notification.error.icon,
    deleteHoverBg: signer.danger.tint,
    deleteHoverBorder: notification.error.icon,
    deleteFontSize: "13.5px",
    deleteGap: "8px",
    deleteIconSize: 15,
    radius: "10px",
    fontWeight: 700,
    spinnerSize: 16,
  },

  /** Full-card placeholder shown to an account with no contacts yet. */
  freshState: {
    padding: "60px 24px",
    iconSize: 72,
    iconBg: signer.orange.tint,
    iconStroke: signer.orange.main,
    iconGlyphSize: 30,
    iconMarginBottom: "20px",
    titleFontSize: "18px",
    titleFontWeight: 800,
    titleColor: signer.ink,
    bodyFontSize: "13.5px",
    bodyColor: signer.text.subtitle,
    bodyMaxWidth: 430,
    bodyLineHeight: 1.65,
    bodyMarginTop: "8px",
    actionsMarginTop: "26px",
    actionsGap: "12px",
    buttonHeight: 46,
    primaryPaddingX: "22px",
    primaryIconSize: 15,
    secondaryPaddingX: "20px",
    secondaryBg: white,
    secondaryColor: signer.text.secondary,
    secondaryBorder: button.secondaryBorderWarm,
    secondaryHoverBorder: signer.ink,
    secondaryHoverColor: signer.ink,
    radius: "10px",
    fontSize: "13.5px",
    fontWeight: 700,
  },

  /** Skeleton fill used while the card loads. */
  skeleton: {
    color: "#ECE9F3",
  },
} as const;

/**
 * Raw media query for the narrow phone column of
 * `Contacts - Mobile & Tablet.dc.html`.
 *
 * The reference draws 375 and 390 differently — the narrow column stacks every
 * form field in one column and runs slightly tighter type and gutters, while
 * the standard column pairs First/Last, Job Title/Company and Zip/Country. MUI
 * has no breakpoint between the two, so the split is expressed as a raw query
 * and applied *after* the `xs` values so it wins.
 */
export const PHONE_SM_QUERY = "@media (max-width:389.98px)";

/**
 * Phone (`xs`) and tablet (`sm`) values, traced from
 * `Contacts - Mobile & Tablet.dc.html`.
 *
 * Only the values that actually differ from the desktop tokens above live
 * here; anything omitted is inherited, so the desktop screen is untouched.
 * `sm` / `std` inside `phone` are the 375 and 390 columns of the reference.
 */
export const contactsResponsiveTokens = {
  phone: {
    /** Content gutters: 14px at 375, 16px at 390. */
    paddingX: { sm: "14px", std: "16px" },
    paddingTop: { sm: "14px", std: "16px" },
    paddingBottom: { sm: "32px", std: "34px" },
    /** Space between the "N total contacts" line and the search field. */
    gap: { sm: "12px", std: "13px" },

    /** "693 total contacts" — the only part of the page header kept here. */
    subtitleFontSize: { sm: "12.5px", std: "13px" },

    /** The reference's search box is white on the page rather than tinted. */
    search: {
      bg: white,
      border: formField.borderWarm,
      radius: "11px",
      padding: "11px 13px",
      gap: "11px",
      iconSize: 16,
      fontSize: { sm: "13px", std: "13.5px" },
    },

    /** One contact row: no selected dot, a trailing chevron instead. */
    row: {
      padding: { sm: "12px 10px", std: "13px 11px" },
      radius: "12px",
      gap: "13px",
      marginBottom: "4px",
      avatarSize: { sm: 40, std: 42 },
      avatarFontSize: { sm: "13px", std: "13.5px" },
      nameFontSize: { sm: "13.5px", std: "14px" },
      emailFontSize: { sm: "11.5px", std: "12px" },
      chevronSize: 15,
      chevronColor: signer.border.strong,
    },

    /** "No contacts match …" block inside the list. */
    empty: {
      padding: "48px 12px",
      titleFontSize: { sm: "13px", std: "13.5px" },
      subFontSize: { sm: "12px", std: "12.5px" },
    },

    /** Detail pane, opened full-screen from a row. */
    detail: {
      padding: { sm: "18px 16px 40px", std: "20px 16px 40px" },
      headGap: { sm: "13px", std: "14px" },
      headMarginBottom: { sm: "20px", std: "22px" },
      avatarSize: { sm: 50, std: 52 },
      avatarFontSize: { sm: "16px", std: "17px" },
      titleFontSize: { sm: "17px", std: "18px" },
      subFontSize: { sm: "12px", std: "12.5px" },
      subMarginTop: "2px",
      /**
       * The reference lays the form out as a flex column; these fold that
       * column gap into the "Address" rule's own 8px/4px margins so the
       * screen's `margin-top` rhythm reproduces it exactly.
       */
      sectionMarginTop: { sm: "22px", std: "23px" },
      sectionMarginBottom: { sm: "18px", std: "19px" },
      sectionFontSize: { sm: "13px", std: "13.5px" },
      /** Same fold for the action stack, which carries no rule on the phone. */
      footerMarginTop: { sm: "28px", std: "31px" },
      footerGap: "9px",
    },

    field: {
      rowGap: { sm: "14px", std: "15px" },
      columnGap: { sm: "12px", std: "14px" },
      height: { sm: 44, std: 46 },
      labelFontSize: { sm: "11.5px", std: "12px" },
      countryFontSize: { sm: "13px", std: "13.5px" },
    },

    formActions: {
      saveHeight: { sm: 46, std: 48 },
      saveFontSize: { sm: "14px", std: "14.5px" },
      deleteFontSize: { sm: "13.5px", std: "14px" },
      deleteIconSize: { sm: 15, std: 16 },
    },

    /** Empty-account card: full-bleed on the page, actions stacked. */
    freshState: {
      padding: { sm: "44px 20px", std: "52px 22px" },
      radius: "16px",
      iconSize: { sm: 62, std: 66 },
      iconGlyphSize: { sm: 26, std: 28 },
      iconMarginBottom: { sm: "16px", std: "17px" },
      titleFontSize: { sm: "16px", std: "17px" },
      bodyFontSize: { sm: "12.5px", std: "13px" },
      actionsMarginTop: { sm: "22px", std: "24px" },
      actionsGap: "9px",
      buttonHeight: 46,
      radiusButton: "11px",
    },

    /** Circled "+" that replaces the two header buttons in the app bar. */
    appBarAction: {
      size: 38,
      radius: "11px",
      iconSize: 19,
      hoverBg: signer.orange.tint,
    },
  },

  tablet: {
    /**
     * The tablet reference runs the master-detail surface edge to edge and
     * viewport-tall, so the page keeps no gutters of its own at all.
     */
    listWidth: 310,
    listBg: white,
    /** Canvas the detail pane sits on, behind its own white form card. */
    detailBg: "#f7f6f2",

    searchPadding: "14px 14px 10px",
    searchRadius: "10px",
    searchFieldPadding: "9px 12px",
    searchGap: "9px",
    searchIconSize: 15,
    searchFontSize: "13px",

    /** "9 SHOWN" strip — kept on the tablet, dropped on the phone. */
    metaPadding: "0 14px 6px",
    metaLetterSpacing: ".06em",

    scrollPadding: "2px 8px 10px",

    rowPadding: "10px 10px",
    rowRadius: "11px",
    rowGap: "12px",
    avatarSize: 36,
    avatarFontSize: "12px",
    nameFontSize: "13px",
    emailFontSize: "11px",
    emailMarginTop: "1px",

    emptyPadding: "40px 16px",
    emptyTitleFontSize: "12.5px",

    detailPadding: "20px 22px 30px",
    headGap: "12px",
    headMarginBottom: "18px",
    detailAvatarSize: 46,
    detailAvatarFontSize: "15px",
    titleFontSize: "16.5px",
    subFontSize: "11.5px",
    subMarginTop: "2px",

    /** White card the form sits in — the phone and desktop have no such box. */
    formCard: {
      bg: white,
      border: contentCard.border,
      radius: "14px",
      padding: "18px",
    },

    /** Folds the form card's 13px column gap into the rule's own margins. */
    sectionMarginTop: "19px",
    sectionMarginBottom: "15px",
    sectionGap: "10px",
    sectionFontSize: "12.5px",

    footerMarginTop: "21px",
    footerPaddingTop: "16px",
    footerGap: "10px",

    field: {
      rowGap: "13px",
      columnGap: "12px",
      labelGap: "5px",
      labelFontSize: "11px",
      height: 40,
      radius: "9px",
      paddingX: "12px",
      /** The number half of the phone control runs 2px tighter. */
      phonePaddingX: "10px",
      fontSize: "13.5px",
      chevronSize: 10,
      countryFontSize: "13px",
    },

    formActions: {
      height: 40,
      radius: "9px",
      savePaddingX: "20px",
      saveFontSize: "13px",
      cancelPaddingX: "16px",
      cancelFontSize: "12.5px",
      deletePaddingX: "14px",
      deleteFontSize: "12.5px",
      deleteGap: "7px",
      deleteIconSize: 14,
    },

    /** Outlined "Add" the app bar carries in place of the page header. */
    appBarAction: {
      height: 38,
      paddingX: "15px",
      radius: "9px",
      fontSize: "13px",
      gap: "8px",
      iconSize: 15,
      hoverBg: signer.warn.tint,
    },
    /** "693 total" beside the screen name in the app bar. */
    appBarCountFontSize: "13px",
    appBarCountColor: signer.text.faint,
    appBarTitleGap: "10px",
  },
} as const;

/**
 * Avatar tints, cycled by list position exactly as the design does
 * (`TINTS[i % TINTS.length]`): a pastel fill with the matching saturated ink.
 */
export const contactAvatarTints = [
  { bg: signer.orange.hover, color: signer.orange.dark },
  { bg: signer.purple.chipBg, color: signer.purple.main },
  { bg: notification.success.bg, color: notification.success.icon },
  { bg: "#E9EEF9", color: "#3E64B0" },
  { bg: notification.error.bg, color: notification.error.icon },
] as const;

export type ContactAvatarTint = (typeof contactAvatarTints)[number];

/** Tint for the contact sitting at `index` in the list. */
export const getContactTint = (index: number): ContactAvatarTint =>
  contactAvatarTints[
    ((index % contactAvatarTints.length) + contactAvatarTints.length) %
      contactAvatarTints.length
  ];

export default contactsTokens;
